import { useEffect, useState } from 'react';
import { cn } from '../lib/cn';

interface ToastProps {
  message: string;
  type?: 'error' | 'success' | 'info';
  duration?: number;
  onClose: () => void;
}

export default function Toast({ message, type = 'info', duration = 2000, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Reset visibility when component mounts or message changes
    setIsVisible(true);
    
    // For error toasts, always use 2000ms
    const actualDuration = type === 'error' ? 2000 : duration;
    
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    
    const timer = setTimeout(() => {
      setIsVisible(false);
      // Wait for fade out animation, then call onClose
      fadeTimer = setTimeout(() => {
        onClose();
      }, 300);
    }, actualDuration);

    return () => {
      clearTimeout(timer);
      if (fadeTimer) {
        clearTimeout(fadeTimer);
      }
    };
  }, [message, duration, type, onClose]);

  return (
    <div
      className={cn(
        'fixed top-20 left-1/2 -translate-x-1/2 z-50',
        'px-4 py-3 rounded-lg shadow-lg',
        'transition-all duration-300',
        'w-[calc(100%-2rem)] max-w-md',
        isVisible ? 'opacity-90 translate-y-0' : 'opacity-0 -translate-y-2',
        type === 'error' && 'bg-red-500 text-white',
        type === 'success' && 'bg-green-500 text-white',
        type === 'info' && 'bg-blue-500 text-white'
      )}
    >
      <p className="text-sm font-medium text-center">{message}</p>
    </div>
  );
}

